import { useState } from 'react';
import { useAuth } from '../../components/AuthProvider';
import { ExternalLink } from 'lucide-react';
import {
  useFoundingPartnerRequests,
  useUpdateFoundingPartnerStatus,
} from '../../hooks/useAdminOps';
import type {
  FoundingPartnerRequestItem,
  FoundingPartnerStatus,
  FoundingPartnerStatusFilter,
  WebsiteLeadAccountFilter,
  WebsiteLeadConsentFilter,
  WebsiteLeadOutreachFilter,
  WebsiteLeadSourceFilter,
} from '../../api/adminOps';
import { QueueTable, StatusChip, DetailDrawer, type QueueColumn } from '../../components/ui';
import {
  ACCOUNT_FILTERS,
  CONSENT_FILTERS,
  FOUNDING_PARTNER_STATUSES,
  OUTREACH_FILTERS,
  PHONE_CONTACT_NOTE,
  SEARCH_DEBOUNCE_MS,
  STATUS_HINTS,
  accountChip,
  consentChip,
  eligibilityChip,
  fullName,
  organizationSizeLabel,
  organizationTypeLabel,
  pipelineTimeline,
  referralSourceLabel,
  safeExternalUrl,
  sourceLabel,
  statusChip,
  statusFilterLabel,
} from '../websiteLeads.logic';
import {
  AccountAndContact,
  ChipFilterGroup,
  ConsentDetail,
  DetailField,
  DetailSection,
  FilterChip,
  LoadError,
  MailLink,
  OutreachCell,
  SearchAndSource,
  absolute,
  relative,
  useDebouncedValue,
} from './parts';
import OutreachSection from './OutreachSection';
import OutreachTools from './OutreachTools';

const STATUS_FILTERS: readonly FoundingPartnerStatusFilter[] = [
  'all',
  ...FOUNDING_PARTNER_STATUSES,
];

/**
 * DM-7703: Founding Partner requests as a follow-up pipeline.
 * DM-8485: plus the outreach log behind each request and the mail-merge
 * sheet round trip for operators.
 */
export default function FoundingPartnersTab() {
  const [status, setStatus] = useState<FoundingPartnerStatusFilter>('new');
  const [source, setSource] = useState<WebsiteLeadSourceFilter>('all');
  const [consent, setConsent] = useState<WebsiteLeadConsentFilter>('all');
  const [account, setAccount] = useState<WebsiteLeadAccountFilter>('all');
  const [outreach, setOutreach] = useState<WebsiteLeadOutreachFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<FoundingPartnerStatus>('new');
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  // The list and the export share these filters, so what is exported is what is shown.
  const filters = {
    status,
    consent,
    account,
    outreach,
    source,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  };
  const queue = useFoundingPartnerRequests(filters);
  const updateMutation = useUpdateFoundingPartnerStatus();
  // UPDATE_FOUNDING_PARTNER_STATUS is a write: auditors read the pipeline only.
  const { hasRole } = useAuth();
  const canWrite = hasRole('admin', 'admin-operator');
  const selected = queue.rows.find(row => row.id === selectedId) ?? null;

  const openRow = (row: FoundingPartnerRequestItem) => {
    setSelectedId(row.id);
    setDraftStatus(row.workflow_status);
  };
  const closeDrawer = () => setSelectedId(null);
  const saveStatus = () => {
    if (!selected) return;
    updateMutation.mutate({ id: selected.id, status: draftStatus }, { onSuccess: closeDrawer });
  };

  const columns: QueueColumn<FoundingPartnerRequestItem>[] = [
    {
      key: 'received',
      header: 'Received',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">{relative(row.last_submitted_at)}</span>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: row => (
        <div>
          <p className="text-white">{row.organization_name}</p>
          <p className="text-xs text-gray-text mt-0.5">
            {[
              organizationTypeLabel(row.organization_type),
              organizationSizeLabel(row.organization_size),
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: row => (
        <div>
          <p className="text-white">{fullName(row)}</p>
          <p className="text-xs text-gray-text mt-0.5">{row.role}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: row => <StatusChip {...statusChip(row.workflow_status)} />,
    },
    {
      key: 'consent',
      header: 'Updates',
      render: row => <StatusChip {...consentChip(row.marketing_consent)} />,
    },
    {
      key: 'account',
      header: 'Account',
      render: row => <StatusChip {...accountChip(row)} />,
    },
    {
      // "Contact" is the requester column on this tab, so the rule gets its own name.
      key: 'contact-rule',
      header: 'Contact rule',
      render: row => <StatusChip {...eligibilityChip(row.contact_eligibility)} />,
    },
    {
      key: 'outreach',
      header: 'Outreach',
      render: row => <OutreachCell summary={row.outreach} />,
    },
    {
      key: 'source',
      header: 'Source',
      render: row => <span className="text-gray-text">{sourceLabel(row.source)}</span>,
    },
  ];

  const counts = queue.summary;
  const website = selected ? safeExternalUrl(selected.website) : null;

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Status">
          {STATUS_FILTERS.map(filter => (
            <FilterChip
              key={filter}
              active={status === filter}
              label={statusFilterLabel(filter)}
              count={
                counts ? (filter === 'all' ? counts.total : counts.by_status[filter]) : undefined
              }
              onClick={() => {
                setStatus(filter);
                setSelectedId(null);
              }}
            />
          ))}
        </div>
        <ChipFilterGroup
          label="Updates"
          options={CONSENT_FILTERS}
          value={consent}
          onChange={value => {
            setConsent(value);
            setSelectedId(null);
          }}
        />
        <ChipFilterGroup
          label="Account"
          options={ACCOUNT_FILTERS}
          value={account}
          onChange={value => {
            setAccount(value);
            setSelectedId(null);
          }}
        />
        <ChipFilterGroup
          label="Outreach"
          options={OUTREACH_FILTERS}
          value={outreach}
          onChange={value => {
            setOutreach(value);
            setSelectedId(null);
          }}
        />
        <SearchAndSource
          idPrefix="founding-partners"
          search={search}
          onSearch={setSearch}
          source={source}
          onSource={setSource}
          placeholder="Search organization, name or email"
        />
        <OutreachTools leadType="founding_partner" params={filters} />
      </div>

      {queue.isError ? (
        <LoadError
          what="Founding Partner requests"
          error={queue.error}
          retry={() => queue.refetch()}
        />
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.id}
          isLoading={queue.isPending}
          onRowClick={openRow}
          rowActionLabel={row => `Open ${row.organization_name}`}
          emptyTitle="No Founding Partner requests"
          emptyHint={
            debouncedSearch
              ? `Nothing matches "${debouncedSearch}".`
              : status === 'all'
                ? 'No organization has asked to become a Founding Partner yet.'
                : `No requests are ${statusFilterLabel(status).toLowerCase()}.`
          }
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      <DetailDrawer
        open={selected !== null}
        title={selected?.organization_name ?? 'Founding Partner request'}
        onClose={closeDrawer}
        footer={
          selected &&
          (canWrite ? (
            <div className="space-y-3">
              <div>
                <label className="label" htmlFor="founding-partner-status">
                  Pipeline status
                </label>
                <select
                  id="founding-partner-status"
                  className="input"
                  value={draftStatus}
                  onChange={event => setDraftStatus(event.target.value as FoundingPartnerStatus)}
                >
                  {FOUNDING_PARTNER_STATUSES.map(option => (
                    <option key={option} value={option}>
                      {statusChip(option).label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-text mt-1">{STATUS_HINTS[draftStatus]}</p>
              </div>
              <button
                onClick={saveStatus}
                disabled={updateMutation.isPending || draftStatus === selected.workflow_status}
                className="btn-primary w-full disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Saving…' : 'Update status'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-text">
              Read only: moving a request through the pipeline needs the admin or operator role.
            </p>
          ))
        }
      >
        {selected && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusChip {...statusChip(selected.workflow_status)} />
              <StatusChip {...consentChip(selected.marketing_consent)} />
              <StatusChip {...accountChip(selected)} />
              <StatusChip label={sourceLabel(selected.source)} tone="neutral" />
            </div>
            <AccountAndContact lead={selected} />
            <OutreachSection leadType="founding_partner" lead={selected} />
            <DetailSection title="Contact">
              <DetailField label="Name" value={fullName(selected)} />
              <DetailField label="Role" value={selected.role} />
              <DetailField label="Email" value={<MailLink email={selected.email} />} />
              <DetailField
                label="Phone"
                value={
                  selected.phone && (
                    <div className="space-y-1">
                      <a href={`tel:${selected.phone}`} className="text-primary hover:underline">
                        {selected.phone}
                      </a>
                      <p className="text-xs text-gray-text">{PHONE_CONTACT_NOTE}</p>
                    </div>
                  )
                }
              />
            </DetailSection>
            <DetailSection title="Organization">
              <DetailField label="Name" value={selected.organization_name} />
              <DetailField label="Type" value={organizationTypeLabel(selected.organization_type)} />
              <DetailField label="Size" value={organizationSizeLabel(selected.organization_size)} />
              <DetailField
                label="Website"
                value={
                  website && (
                    <a
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1 break-all"
                    >
                      {selected.website}
                      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                    </a>
                  )
                }
              />
              <DetailField
                label="Current donor management"
                value={
                  selected.current_donor_management && (
                    <span className="whitespace-pre-wrap">{selected.current_donor_management}</span>
                  )
                }
              />
            </DetailSection>
            <DetailSection title="Follow-up">
              <ol className="space-y-1">
                {pipelineTimeline(selected).map(stage => (
                  <li key={stage.label} className="text-sm flex justify-between gap-3">
                    <span className="text-white">{stage.label}</span>
                    <span className="text-gray-text">{absolute(stage.at)}</span>
                  </li>
                ))}
              </ol>
            </DetailSection>
            <DetailSection title="Attribution & consent">
              <ConsentDetail consent={selected.marketing_consent} />
              <DetailField
                label="Heard about us"
                value={referralSourceLabel(selected.referral_source)}
              />
              <DetailField label="Referral code" value={selected.referral_code} />
              <DetailField label="Submitted from" value={selected.source_path} />
              <DetailField label="Form version" value={selected.form_version} />
              <DetailField label="Last submitted" value={absolute(selected.last_submitted_at)} />
            </DetailSection>
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
